import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  Box,
  Chip,
  Dialog,
  Divider,
  InputAdornment,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  TextField,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import DarkModeOutlinedIcon from '@mui/icons-material/DarkModeOutlined';
import KeyboardOutlinedIcon from '@mui/icons-material/KeyboardOutlined';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import PolicyOutlinedIcon from '@mui/icons-material/PolicyOutlined';
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import { useQueries } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { NAV_ITEMS } from '../../nav';
import { isNavItemVisible } from '../../navVisibility';
import { useNavAuth } from '../../hooks/useNavAuth';
import { useAuth, hasRole } from '../../auth/AuthProvider';
import { useThemeMode } from '../../theme/ThemeModeProvider';
import { useRecentItems } from '../../hooks/useRecentItems';
import { useLocalStorageState } from '../../hooks/useLocalStorageState';
import { endpoints } from '../../api/endpoints';
import { highlight } from '../../utils/highlight';

interface Command {
  id: string;
  label: string;
  sublabel?: string;
  group: string;
  icon?: ReactNode;
  run: () => void;
}

export function CommandPalette({
  open,
  onClose,
  onOpenHelp,
}: {
  open: boolean;
  onClose: () => void;
  onOpenHelp: () => void;
}) {
  const navigate = useNavigate();
  const { role, user, tenantId } = useAuth();
  // Built once, in `useNavAuth`, so the sidebar and the palette cannot show
  // different menus. They already diverged once — see that hook.
  const navAuth = useNavAuth();
  const { toggle } = useThemeMode();
  const { items: recent, push: pushRecent } = useRecentItems();
  const [recentSearches, setRecentSearches] = useLocalStorageState<string[]>(
    'oip.recentSearches',
    [],
  );

  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const [selected, setSelected] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      setQuery('');
      setDebounced('');
      setSelected(0);
      setTimeout(() => inputRef.current?.focus(), 40);
    }
  }, [open]);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(query.trim()), 250);
    return () => clearTimeout(t);
  }, [query]);

  const searching = debounced.length >= 2;
  const isAdmin = hasRole(role, 'TENANT_ADMIN') || !!user?.isPlatformAdmin;

  const searchResults = useQueries({
    queries: [
      {
        queryKey: ['cmd', 'incidents', tenantId, debounced],
        queryFn: () => endpoints.incidents({ search: debounced, pageSize: 5 }),
        enabled: open && searching,
      },
      {
        queryKey: ['cmd', 'vehicles', tenantId, debounced],
        queryFn: () => endpoints.vehicles({ search: debounced, pageSize: 5 }),
        enabled: open && searching,
      },
      {
        queryKey: ['cmd', 'recommendations', tenantId, debounced],
        queryFn: () => endpoints.recommendations({ search: debounced, pageSize: 5 }),
        enabled: open && searching,
      },
      {
        queryKey: ['cmd', 'policies', tenantId, debounced],
        queryFn: () => endpoints.policies({ search: debounced, pageSize: 5 }),
        enabled: open && searching,
      },
      {
        queryKey: ['cmd', 'users', tenantId, debounced],
        queryFn: () => endpoints.users({ search: debounced, pageSize: 5 }),
        enabled: open && searching && isAdmin,
      },
    ],
  });

  const go = (
    path: string,
    remember?: {
      type: 'incident' | 'vehicle' | 'recommendation' | 'policy';
      id: string;
      label: string;
    },
  ) => {
    if (debounced)
      setRecentSearches((s) => [debounced, ...s.filter((x) => x !== debounced)].slice(0, 6));
    if (remember) pushRecent({ ...remember, path });
    navigate(path);
    onClose();
  };

  const commands = useMemo<Command[]>(() => {
    const list: Command[] = [];
    if (!searching) {
      list.push(
        {
          id: 'act-theme',
          label: 'Toggle light / dark theme',
          group: 'Actions',
          icon: <DarkModeOutlinedIcon fontSize="small" />,
          run: () => {
            toggle();
            onClose();
          },
        },
        {
          id: 'act-help',
          label: 'Keyboard shortcuts',
          group: 'Actions',
          icon: <KeyboardOutlinedIcon fontSize="small" />,
          run: () => {
            onClose();
            onOpenHelp();
          },
        },
      );
      for (const item of recent) {
        list.push({
          id: `recent-${item.path}`,
          label: item.label,
          sublabel: item.type,
          group: 'Recently viewed',
          icon: <HistoryOutlinedIcon fontSize="small" />,
          run: () => go(item.path),
        });
      }
    }
    /*
     * Navigation, filtered by exactly what the sidebar shows.
     *
     * Through `isNavItemVisible`, not a local comparison. This loop used to
     * check `roles` alone, which ignored both of the other gates: every tenant
     * user was offered the whole PLATFORM section by name — Tenants, Users,
     * Platform Audit — and every feature-gated page whether or not the tenant
     * held the entitlement. The routes refused them, so no data escaped, but a
     * palette that lists screens you may not open is not a menu, and it
     * advertised the platform surface to every customer.
     */
    for (const item of NAV_ITEMS) {
      if (!isNavItemVisible(item, navAuth)) continue;
      if (searching && !item.label.toLowerCase().includes(debounced.toLowerCase())) continue;
      list.push({
        id: `nav-${item.path}`,
        label: item.label,
        group: 'Go to',
        icon: item.icon,
        run: () => go(item.path),
      });
    }
    if (searching) {
      const [inc, veh, rec, pol, usr] = searchResults;
      for (const i of inc.data?.items ?? [])
        list.push({
          id: `inc-${i.id}`,
          label: i.title,
          sublabel: i.vehicle.name,
          group: 'Incidents',
          icon: <WarningAmberOutlinedIcon fontSize="small" />,
          run: () => go(`/incidents/${i.id}`, { type: 'incident', id: i.id, label: i.title }),
        });
      for (const v of veh.data?.items ?? [])
        list.push({
          id: `veh-${v.id}`,
          label: v.name,
          sublabel: v.registration,
          group: 'Vehicles',
          icon: <LocalShippingOutlinedIcon fontSize="small" />,
          run: () => go(`/vehicles/${v.id}`, { type: 'vehicle', id: v.id, label: v.name }),
        });
      for (const r of rec.data?.items ?? [])
        list.push({
          id: `rec-${r.id}`,
          label: r.recommendedAction,
          sublabel: r.actionCategory,
          group: 'Recommendations',
          icon: <LightbulbOutlinedIcon fontSize="small" />,
          run: () =>
            go(`/recommendations/${r.id}`, {
              type: 'recommendation',
              id: r.id,
              label: r.recommendedAction,
            }),
        });
      for (const p of pol.data?.items ?? [])
        list.push({
          id: `pol-${p.id}`,
          label: p.name,
          sublabel: p.eventType,
          group: 'Policies',
          icon: <PolicyOutlinedIcon fontSize="small" />,
          run: () => go('/policies', { type: 'policy', id: p.id, label: p.name }),
        });
      for (const u of usr.data?.items ?? [])
        list.push({
          id: `usr-${u.userId}`,
          label: u.displayName,
          sublabel: u.email,
          group: 'Members',
          icon: <GroupOutlinedIcon fontSize="small" />,
          run: () => go('/admin/members'),
        });
      if (
        'prompts'.includes(debounced.toLowerCase()) ||
        'registry'.includes(debounced.toLowerCase())
      )
        list.push({
          id: 'prompts',
          label: 'Prompt Registry',
          group: 'Go to',
          icon: <AutoAwesomeOutlinedIcon fontSize="small" />,
          run: () => go('/prompts'),
        });
    }
    return list;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    searching,
    debounced,
    recent,
    role,
    user,
    navAuth,
    searchResults.map((r) => r.dataUpdatedAt).join(','),
  ]);

  useEffect(() => setSelected(0), [debounced]);

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelected((s) => Math.min(s + 1, commands.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelected((s) => Math.max(s - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      commands[selected]?.run();
    }
  };

  // group items in order of first appearance
  const groups: { group: string; items: { cmd: Command; index: number }[] }[] = [];
  commands.forEach((cmd, index) => {
    let g = groups.find((x) => x.group === cmd.group);
    if (!g) {
      g = { group: cmd.group, items: [] };
      groups.push(g);
    }
    g.items.push({ cmd, index });
  });

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ 'aria-label': 'Command palette', sx: { position: 'absolute', top: 72, m: 0 } }}
    >
      <Box sx={{ p: 1.5, pb: 1 }}>
        <TextField
          fullWidth
          size="small"
          inputRef={inputRef}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder="Search incidents, vehicles, policies… or jump to a page"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        {!searching && recentSearches.length > 0 && (
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 1 }}>
            {recentSearches.map((s) => (
              <Chip key={s} size="small" label={s} onClick={() => setQuery(s)} />
            ))}
          </Box>
        )}
      </Box>
      <Divider />
      <Box sx={{ maxHeight: 420, overflowY: 'auto' }}>
        {commands.length === 0 ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              {searching ? 'No matches. Try a different term.' : 'Start typing to search.'}
            </Typography>
          </Box>
        ) : (
          groups.map((g) => (
            <Box key={g.group}>
              <Typography
                variant="overline"
                color="text.secondary"
                sx={{ px: 2, pt: 1, display: 'block' }}
              >
                {g.group}
              </Typography>
              <List dense disablePadding>
                {g.items.map(({ cmd, index }) => (
                  <ListItemButton
                    key={cmd.id}
                    selected={index === selected}
                    onMouseEnter={() => setSelected(index)}
                    onClick={() => cmd.run()}
                  >
                    {cmd.icon && <ListItemIcon sx={{ minWidth: 34 }}>{cmd.icon}</ListItemIcon>}
                    <ListItemText
                      primary={searching ? highlight(cmd.label, debounced) : cmd.label}
                      secondary={cmd.sublabel}
                      primaryTypographyProps={{ variant: 'body2', noWrap: true }}
                      secondaryTypographyProps={{ variant: 'caption', noWrap: true }}
                    />
                  </ListItemButton>
                ))}
              </List>
            </Box>
          ))
        )}
      </Box>
    </Dialog>
  );
}
